import { defineStore } from 'pinia';
import ProductService from '@/services/product';
import LicenceUtil from '@/utils/licence';
import { BotService } from '@/services/bot';
import { getRidOfTabs } from '@/utils/stringUtils';
import SettingService from '@/services/settings';
import _ from 'lodash';
import { useAuthStore } from '@/stores/auth';
import { useDashboardStore } from '@/stores/dashboard';
import { useIntentsStore } from '@/stores/intents';
import { usePreferencesStore } from '@/stores/preferences';

/** State of the bots module */
export interface BotsState {
  /** The currently selected bot (full bot object incl. `uniqueBotId`, `channels`, `licences`, ...), or `null` */
  currentBot: any;
  /** Whether the hub currently shows the staging bot instead of the live bot */
  viewStaging: boolean;
  /** All bots the user has access to (only bots that have a staging bot) */
  bots: any[];
  /** Detailed bot objects (incl. `settings`, `nlpFeatures`, ...) keyed by unique bot ID */
  botDetails: Record<string, any>;
  /** Channel ID currently selected in the UI, or `null` */
  currentChannelId: string | null;
}

/**
 * Internal helper shared by the `fetchRegExp` and `setRegExp` actions — applies a fetched/saved
 * regexp array to the matching entry in `bots` and (if it's the current bot) to `currentBot`.
 *
 * Extracted out of the store so `setRegExp` (a merged action+mutation, see below) can reuse the
 * exact state-application logic that `fetchRegExp` also needs, without `fetchRegExp` re-triggering
 * the `BotService.setRegExp` network PUT that `setRegExp` performs. This is the same logic the
 * legacy `setRegExp` MUTATION contained; it is not itself exposed as a store action.
 */
function applyRegExpToState(store: any, botId: string, regexp: any) {
  let regexpArray = regexp ?? [];
  regexpArray = _.isArray(regexpArray) ? regexpArray : [regexpArray];

  const bot = store.bots.find((bot: any) => bot.uniqueBotId === botId);
  if (!bot) return;

  if (store.currentBot && store.currentBot.uniqueBotId === botId) {
    store.currentBot = { ...store.currentBot, regexp: regexpArray };
  }

  bot.regexp = regexpArray;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/bots.ts` (namespace `bots`).
 * Persisted to `sessionStorage` (full state) — matches the legacy `vuexSession` reducer's
 * `bots: state.bots` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Pinia forbids a getter sharing a name with a state property (`console.warn`/breakage at
 *   runtime). Legacy had 4 such collisions — the getters are renamed `<name>Getter`, state stays
 *   readable directly under its original name:
 *   - getter `bots` → `botsGetter` (was a pure passthrough of `state.bots`)
 *   - getter `currentBot` → `currentBotGetter` (nulls out non-object values; NOT a pure
 *     passthrough, but the name collision still has to be resolved)
 *   - getter `currentChannelId` → `currentChannelIdGetter` (pure passthrough)
 *   - getter `viewStaging` → `viewStagingGetter` (pure passthrough)
 * - `setViewStaging`, `setNlpTemplate`, `setRegExp`: action + mutation of the same name merged
 *   into one action each (mutation body inlined into the action).
 * - All other legacy mutations (no action of the same name existed) become same-named actions:
 *   `setBotDetail`, `setBots`, `setCurrentBot`, `reloadBot`, `setCurrentChannelId`, `setLicences`,
 *   `setSettings`, `setChannelActiveState`, `setFallbackFaqIntent`, `setToneOfVoice`,
 *   `setMarkdownToggle`, `setChannel`.
 * - Cross-namespace access, resolved via sibling composables called INSIDE the relevant
 *   getter/action body:
 *   - getter `currentChannels`: `rootGetters['auth/user']` → `useAuthStore().user`.
 *   - action `setCurrentBot`: `store.dispatch('dashboard/clear')` / `store.dispatch('intents/clear')`
 *     → `useDashboardStore().clear()` / `useIntentsStore().clear()`.
 *   - action `setCurrentChannelId`: `this.dispatch('preferences/setLastChannelId', payload)` →
 *     `usePreferencesStore().setLastChannelId(payload)` (legacy mutation name unchanged; only the
 *     unrelated `preferences` getter/action pair `lastSelectedChannelId` was renamed there).
 */
export const useBotsStore = defineStore('bots', {
  state: (): BotsState => ({
    currentBot: null,
    viewStaging: false,
    bots: [],
    botDetails: {},
    currentChannelId: null,
  }),
  getters: {
    /** Renamed from legacy getter `bots` (collided with the `bots` state prop). */
    botsGetter(state: BotsState): any[] {
      return state.bots;
    },
    /** Renamed from legacy getter `currentBot` (collided with the `currentBot` state prop). */
    currentBotGetter(state: BotsState): any {
      return typeof state.currentBot === 'object' ? state.currentBot : null;
    },
    /** Renamed from legacy getter `currentChannelId` (collided with the `currentChannelId` state prop). */
    currentChannelIdGetter(state: BotsState): string | null {
      return state.currentChannelId;
    },
    currentChannels(): any[] {
      const channels = this.currentBotGetter ? this.currentBotGetter.channels : [];
      const user = useAuthStore().user;
      return channels.map((channel: any) => {
        const channelCopy = JSON.parse(JSON.stringify(channel));
        if (!channelCopy.displayName) {
          channelCopy.displayName = channelCopy.channelId;
        }
        if (user && Array.isArray(user.blockedChannels)) {
          channelCopy.blocked = user.blockedChannels.includes(
            channelCopy.channelId
          );
        }
        return channelCopy;
      });
    },
    currentContext(state: BotsState): any {
      const currentChannel = this.currentChannels.find(
        (channel: any) => channel.channelId === state.currentChannelId
      );
      return currentChannel ? currentChannel.context : state.currentBot?.context || '';
    },
    areChannelsBlocked(): boolean {
      return this.currentChannels.filter((channel: any) => channel.blocked).length > 0;
    },
    currentBotId(state: BotsState): string | null {
      return state.currentBot ? state.currentBot.uniqueBotId : null;
    },
    viewableBotId(state: BotsState): string | null {
      return state.currentBot
        ? state.viewStaging
          ? state.currentBot.stagingBot
          : state.currentBot.uniqueBotId
        : null;
    },
    /** Renamed from legacy getter `viewStaging` (collided with the `viewStaging` state prop). */
    viewStagingGetter(state: BotsState): boolean {
      return state.viewStaging;
    },
    getBot(state: BotsState) {
      return (botId: string) => state.botDetails[botId];
    },
    getBotSettings(state: BotsState) {
      return (botId: string) => {
        const bots = state.botDetails;
        if (!bots[botId]) return false;
        return bots[botId].settings;
      };
    },
    getNLPTemplate(state: BotsState): any {
      return state.currentBot && state.currentBot.nlpTemplate
        ? state.currentBot.nlpTemplate
        : null;
    },
    getAllRegExp(state: BotsState): any {
      return state.currentBot && state.currentBot.regexp
        ? state.currentBot.regexp
        : null;
    },
    // TODO: remove this
    getChannelById(state: BotsState) {
      return (botId: string, channelId: string) => {
        const chan = state.botDetails[botId].channels.filter(
          (channel: any) => channel.channelId === channelId
        );
        return chan[0];
      };
    },
    getCurrentChannelById(state: BotsState) {
      return (channelId: string) => {
        if (!state.currentBot) return null;
        const channel = state.currentBot.channels.filter(
          (channel: any) => channel.channelId === channelId
        );
        if (channel.length > 0) {
          return channel[0];
        }
        return null;
      };
    },
    getCurrentChannelConfig(state: BotsState) {
      return (channelId: string) => {
        const channelConfig = state.currentBot.channels.filter(
          (channel: any) => channel.channelId === channelId
        );
        if (channelConfig.length > 0) {
          return channelConfig[0].config;
        }
        return {};
      };
    },
    currentBotContactId(state: BotsState): any {
      if (
        !state.botDetails ||
        !state.currentBot ||
        !state.currentBot.uniqueBotId
      ) {
        return null;
      }
      const botId = state.currentBot.uniqueBotId;

      if (
        !state.botDetails[botId] ||
        !state.botDetails[botId].settings ||
        !state.botDetails[botId].settings.contact
      ) {
        return null;
      }

      return state.botDetails[botId].settings.contact.contactId;
    },
    currentBotStage(state: BotsState): any {
      if (
        !state.botDetails ||
        !state.currentBot ||
        !state.currentBot.uniqueBotId
      ) {
        return 'undefined';
      }
      const botId = state.currentBot.uniqueBotId;

      if (
        !state.botDetails[botId] ||
        !state.botDetails[botId].settings ||
        !state.botDetails[botId].settings.stage
      ) {
        return 'undefined';
      }

      return state.botDetails[botId].settings.stage;
    },
    currentLicence(state: BotsState): any {
      return LicenceUtil.currentLicence(state.currentBot?.licences);
    },
    hasWidget(state: BotsState): boolean {
      return !!state.currentBot?.channels?.find((c: any) => c.channel === 'widget');
    },
    hasKeywordMatchFeature(state: BotsState): boolean {
      if (
        !state.botDetails ||
        !state.currentBot ||
        !state.currentBot.uniqueBotId
      ) {
        return false;
      }

      const botId = state.currentBot.uniqueBotId;

      if (!state.botDetails[botId] || !state.botDetails[botId].nlpFeatures) {
        return false;
      }

      return !!state.botDetails[botId].nlpFeatures.find(
        (f: string) => f === 'keywordmatch'
      );
    },
    hasLLMLegacyModel(state: BotsState): boolean {
      if (
        !state.botDetails ||
        !state.currentBot ||
        !state.currentBot.uniqueBotId
      ) {
        return false;
      }

      const botId = state.currentBot.uniqueBotId;
      if (!state.botDetails[botId] || !state.botDetails[botId].nlpFeatures) {
        return false;
      }

      return !!state.botDetails[botId].nlpFeatures.find((f: string) => f === 'k2legacy');
    },
    hasLegacyModel(state: BotsState): boolean {
      if (
        !state.botDetails ||
        !state.currentBot ||
        !state.currentBot.uniqueBotId
      ) {
        return false;
      }

      const botId = state.currentBot.uniqueBotId;

      if (!state.botDetails[botId] || !state.botDetails[botId].nlpFeatures) {
        return false;
      }

      return !!state.botDetails[botId].nlpFeatures.find((f: string) => f === 'k1');
    },
    /**
     * Get the staging bot for a given bot ID
     */
    getStagingBot(state: BotsState) {
      return (botId: string): any | null => {
        const bot = state.botDetails[botId];
        if (!bot?.stagingBot) return bot; // It is the staging bot

        return state.botDetails?.[bot?.stagingBot] || null;
      };
    },
  },
  actions: {
    async get(): Promise<boolean> {
      const bots = await BotService.get();
      this.setBots(bots);
      return true;
    },
    async changeBot(payload: { uniqueBotId: string; router: any }) {
      const { uniqueBotId, router } = payload;
      // Just set bid and reload app
      let query = Object.assign({}, router.query);
      query.bid = uniqueBotId;
      router.replace({ query }).catch(() => true);
      await new Promise((resolve) => setTimeout(resolve, 200));
      // Legacy `forceReload` argument (not in current DOM typings), kept for identical runtime behavior
      (window.location as any).reload(true);
    },
    check() {
      if (this.bots.length > 0) {
        this.setCurrentBot(this.bots[0]);
      }
    },
    /** Merged from legacy action `setViewStaging` (which only committed the mutation of the same name). */
    setViewStaging(view: boolean) {
      this.viewStaging = view;
    },
    clear() {
      this.setCurrentBot(null);
      this.setBots([]);
    },
    async getBotById(botId: string) {
      const bot = await BotService.getBot(botId);
      // fetch nlp pipe infos
      const featureResult = await BotService.getNlPFeatures(botId);
      if (featureResult && featureResult.status === 'ok') {
        bot.nlpFeatures = featureResult.features;
      }

      this.setBotDetail({ botId, bot });

      const stagingBot = this.currentBot?.stagingBot || undefined;
      const stagingBotFor = this.currentBot?.stagingBotFor || undefined;
      if (
        this.currentBot &&
        this.currentBot.uniqueBotId !== botId &&
        (stagingBot === undefined || stagingBot !== botId) &&
        (stagingBotFor === undefined || stagingBotFor !== botId)
      ) {
        this.setCurrentBot(bot);
      } else if (!this.currentBot && bot.stagingBot) {
        this.setCurrentBot(bot);
      }
      return bot;
    },
    async updateLicences(botId: string) {
      const bot = await BotService.getBot(botId);
      if (this.currentBot && this.currentBot.uniqueBotId === botId) {
        this.setLicences(bot);
      } else {
        this.setCurrentBot(bot);
      }
    },
    /**
     * Merged from legacy action `setNlpTemplate` (bot lookup + API call) + legacy mutation
     * `setNlpTemplate` of the same name (state application). The mutation re-did the same `bots`
     * lookup the action already performed; that redundant second lookup is dropped here.
     */
    async setNlpTemplate(payload: { botId: string; templateBotId: string; displayName: string }) {
      const { botId, templateBotId, displayName } = payload;
      const bot = this.bots.find((bot: any) => bot.uniqueBotId === botId);
      if (!bot) {
        return;
      }

      const result = await ProductService.setNlpTemplate(botId, {
        displayName: displayName,
        botId: templateBotId,
      });
      if (result && result.status === 'ok') {
        if (this.currentBot && this.currentBot.uniqueBotId === botId) {
          this.currentBot.nlpTemplate = {
            botId: templateBotId,
            displayName: displayName,
          };
        }

        bot.nlpTemplate = {
          botId: templateBotId,
          displayName: displayName,
        };
      }
    },
    async addNewAlias(payload: { botId: string; alias: any }) {
      const { botId, alias } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };

      mergedSettings.notifications.mail[getRidOfTabs(alias.name)] = {
        ...alias,
        name: getRidOfTabs(alias.name),
        subject: getRidOfTabs(alias.subject),
        receipients: alias.receipients.map((r: any) => ({
          Email: getRidOfTabs(r.Email),
        })),
        replyTo: Boolean(alias.replyTo ?? true), // Should be true as default if not provided
      };

      try {
        const newSettings = await BotService.saveSettings(botId, { notifications: mergedSettings.notifications });
        this.setSettings({ botId, settings: newSettings });
        return 'success';
      } catch (error) {
        return 'error';
      }
    },
    async setSettingsByKey(payload: { botId: string; key: string; config: any; strict?: boolean }) {
      const { botId, key, config, strict = false } = payload;
      if (!key) return false;

      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings[key] = config;

      try {
        // Only updated config (not whole bot.settings)
        const newSettings = !strict
          ? await BotService.saveSettings(botId, { [key]: config })
          : await BotService.saveSettings(botId, mergedSettings[key], key);

        this.setSettings({ botId, settings: !strict ? newSettings : mergedSettings });
        return true;
      } catch (_) {
        return false;
      }
    },
    async setContact(payload: { botId: string; contactId: string }) {
      const { botId, contactId } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      if (!mergedSettings.contact) {
        mergedSettings.contact = {};
      }
      mergedSettings.contact.contactId = contactId;
      try {
        const newSettings = await BotService.saveSettings(botId, { contact: mergedSettings.contact });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (_) {
        return false;
      }
    },
    async setStage(payload: { botId: string; stage: string }) {
      const { botId, stage } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings.stage = stage;
      try {
        const newSettings = await BotService.saveSettings(botId, { stage: mergedSettings.stage });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (_) {
        return false;
      }
    },
    async setCleanup(payload: { botId: string; cleanup: any }) {
      const { botId, cleanup } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings.cleanup = cleanup;
      try {
        const newSettings = await BotService.saveSettings(botId, { cleanup: mergedSettings.cleanup });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (error) {
        return false;
      }
    },
    async setQuota(payload: { botId: string; quota: any }) {
      const { botId, quota } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings.quota = quota;
      try {
        const newSettings = await BotService.saveSettings(botId, { quota: mergedSettings.quota });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (error) {
        return false;
      }
    },
    async setTrafficMonitoring(payload: { botId: string; isActive: boolean }) {
      const { botId, isActive } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings.trafficMonitoring = { active: isActive };
      try {
        const newSettings = await BotService.saveSettings(botId, { trafficMonitoring: mergedSettings.trafficMonitoring });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (error) {
        return false;
      }
    },
    async setBotActive(payload: { botId: string; active: boolean }) {
      const { botId, active } = payload;
      try {
        const data = await BotService.setBotActive(botId, active);
        this.setBotActiveState({ botId, ...data });
        return { success: true };
      } catch (error: any) {
        return { success: false, error };
      }
    },
    async setDisableLivechatStatistics(payload: { botId: string; isActive: boolean }) {
      const { botId, isActive } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      mergedSettings.moin_livechat.disableStatistics = isActive;
      try {
        const newSettings = await BotService.saveSettings(botId, { moin_livechat: mergedSettings.moin_livechat });
        this.setSettings({ botId, settings: newSettings });
        return true;
      } catch (error) {
        return false;
      }
    },
    async deleteAlias(payload: { botId: string; aliasId: string }) {
      const { botId, aliasId } = payload;
      const mergedSettings = { ...this.botDetails[botId].settings };
      delete mergedSettings.notifications.mail[aliasId];
      try {
        const newSettings = await BotService.saveSettings(botId, { notifications: mergedSettings.notifications });
        this.setSettings({ botId, settings: newSettings });
        return 'success';
      } catch (error) {
        return 'error';
      }
    },

    /**
     * @typedef {{ type: 'regexp', displayName: string, expression: string, parameter: string }} RegexpEntity
     *
     * @param {{ botId: string }} params
     * @returns {RegexpEntity[]}
     */
    async fetchRegExp(payload: { botId: string }) {
      const { botId } = payload;
      const regexpArray = await BotService.getAllRegEpx(botId);
      applyRegExpToState(this, botId, regexpArray);

      return regexpArray;
    },
    /**
     * Merged from legacy action `setRegExp` (API call) + legacy mutation `setRegExp` of the same
     * name (state application, factored into the shared `applyRegExpToState` helper above).
     *
     * @param {{
     *  botId: string,
     *  regexp: RegexpEntity[],
     * }} param
     * @returns {RegexpEntity[]}
     */
    async setRegExp(payload: { botId: string; regexp: any }) {
      const { botId, regexp } = payload;
      if (!regexp) return;
      const regexpArr = _.isArray(regexp)
        ? regexp
        : [regexp];

      // Updated
      // Cast to any: BotService.setRegExp returns a discriminated union where `data`/`error`
      // only exist on one branch each; legacy code (under Vuex's untyped `any` context)
      // destructured both unconditionally, relying on `undefined` for the missing field.
      const { success, data, error } = await BotService.setRegExp(botId, regexpArr) as any;
      if (success) applyRegExpToState(this, botId, data);

      return { success, data, error };
    },

    /**
     * Fetch the Tone of Voice settings from the backend, populating any defaults if you have nothing
     * @param botId The ID of the bot you want to use, if not the current one
     * @throws {Error} If the API responds with error or there is no bot active
     * @returns Promise of `{channelId, enabled, examples: {scenario, answer}[]}[]`
     */
    async fetchToneOfVoice(botId: string | null = null) {
      if (!botId) {
        if (!this.currentBot?.uniqueBotId) throw new Error("No current bot");
        botId = this.currentBot.uniqueBotId;
      }

      // Enforce always live bot
      if (this.botDetails[botId as string].stagingBotFor) botId = this.botDetails[botId as string].stagingBotFor;

      // Cast to any: legacy code (under Vuex's untyped `any` context) checked
      // `channelTones.error`, but the service's real return type (`ToneOfVoiceChannelSettings[] | null`)
      // never carries an `.error` field. Preserved as-is, including the latent behavior of
      // throwing if the service returns `null` (SettingService already logs the error itself).
      const channelTones = await SettingService.getToneOfVoiceSettings(botId as string) as any;

      if (channelTones.error) throw new Error(channelTones.error);
      this.setToneOfVoice({ botId: botId as string, channelTones });

      return channelTones;
    },
    /**
     * Stores the Tone of Voice settings for a given channel in the backend
     * @param botId The ID of the bot
     * @param channelId The ID of the channel, or `null` for the default/first channel
     * @param enabled Whether the tone of voice feature is enabled or not
     * @param rules The tone-of-voice rules to store
     * @returns The entire channel's new state
     */
    async putToneOfVoice(payload: { botId: string; channelId: string | null; enabled: boolean; rules: any }) {
      const { botId, channelId, enabled, rules } = payload;
      const channel = await SettingService.setToneOfVoiceSettings(botId, channelId, enabled, rules);
      // The service swallows request errors and returns null (MOIN-HUB-2H: `reading 'toneOfVoice'`)
      if (!channel) throw new Error(`Saving the tone of voice for channel ${channelId} failed`);

      const channelTones = [{
        channelId,
        ...channel.toneOfVoice,
      }];
      this.setToneOfVoice({ botId, channelTones });

      return channel;
    },

    /** Converted from legacy mutation `setBotDetail` (no matching legacy action existed). */
    setBotDetail(data: { botId: string; bot: any }) {
      this.botDetails[data.botId] = data.bot;
    },
    /** Converted from legacy mutation `setBots` (no matching legacy action existed). */
    setBots(bots: any[]) {
      // Sort ASC
      bots.sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        if (a.name.toLowerCase() === b.name.toLowerCase()) return 0;
        return 1;
      });

      bots = bots.filter(
        (b) => b.stagingBot // only bots with staging bot
      );

      this.bots = bots;
    },
    /**
     * Converted from legacy mutation `setCurrentBot` (no matching legacy action existed).
     * `store.dispatch('dashboard/clear')` / `store.dispatch('intents/clear')` → sibling composables.
     */
    setCurrentBot(bot: any) {
      useDashboardStore().clear();
      useIntentsStore().clear();
      this.currentBot = bot;
    },
    /** Converted from legacy mutation `reloadBot` (no matching legacy action existed). */
    reloadBot() {
      if (this.currentBot) {
        const bot = this.botDetails[this.currentBot.uniqueBotId];
        if (bot) {
          this.currentBot = bot;
        }
      }
    },
    /**
     * Converted from legacy mutation `setCurrentChannelId` (no matching legacy action existed).
     * `this.dispatch('preferences/setLastChannelId', payload)` → `usePreferencesStore()`.
     */
    setCurrentChannelId(channelId: string | null) {
      this.currentChannelId = channelId;

      const botId = this.currentBot ? this.currentBot.uniqueBotId : null;
      if (!botId) return;

      // Propagate changes to preferences.
      usePreferencesStore().setLastChannelId({ channelId, botId });
    },
    /** Converted from legacy mutation `setLicences` (no matching legacy action existed). */
    setLicences(bot: any) {
      if (this.currentBot) {
        this.currentBot.licences = bot.licences;
      }
    },
    /** Converted from legacy mutation `setSettings` (no matching legacy action existed). */
    setSettings(payload: { botId: string; settings: any }) {
      this.botDetails[payload.botId].settings = payload.settings;
    },
    setBotActiveState(payload: {
      botId: string;
      active: boolean;
      deactivatedAt: string | null;
      channels?: { channelId: string; active: boolean }[];
    }) {
      const { botId, active, deactivatedAt, channels = [] } = payload;
      const bots = [this.botDetails[botId]];
      if (this.currentBot?.uniqueBotId === botId) bots.push(this.currentBot);

      bots.filter(Boolean).forEach((bot: any) => {
        bot.active = active;
        bot.deactivatedAt = deactivatedAt;
        channels.forEach(({ channelId, active: channelActive }) => {
          const chan = bot.channels?.find((c: any) => c.channelId === channelId);
          if (chan) chan.active = channelActive;
        });
      });
    },

    /** Converted from legacy mutation `setChannelActiveState` (no matching legacy action existed). */
    setChannelActiveState(payload: { botId: string; channelId: string; activeState: boolean }) {
      const { botId, channelId, activeState } = payload;
      // set state in bot details and current bot
      if (this.botDetails[botId]) {
        const chan = this.botDetails[botId].channels.filter(
          (channel: any) => channel.channelId === channelId
        );
        if (chan && chan.length > 0) {
          chan[0].active = activeState;
        }
      }

      if (this.currentBot) {
        const chan = this.currentBot.channels.filter(
          (channel: any) => channel.channelId === channelId
        );
        if (chan && chan.length > 0) {
          chan[0].active = activeState;
        }
      }
    },

    /** Converted from legacy mutation `setFallbackFaqIntent` (no matching legacy action existed). */
    setFallbackFaqIntent(fallbackFaqIntentObject: any) {
      if (!this.currentBot) return;
      this.currentBot.fallbackFaqIntent = fallbackFaqIntentObject;
      this.botDetails[this.currentBot.uniqueBotId].fallbackFaqIntent = fallbackFaqIntentObject;
    },
    /** Converted from legacy mutation `setToneOfVoice` (no matching legacy action existed). */
    setToneOfVoice(payload: { botId: string; channelTones: any[] }) {
      const { botId, channelTones } = payload;
      let bot = this.botDetails[botId];

      // Always target the live bot
      if (bot.stagingBotFor) bot = this.botDetails[bot.stagingBotFor];
      if (!bot) return;

      // Helper function for applying the changes
      const applyChannelChanges = (bot: any, channelTones: any[]) => {
        // For each channel we received, overwrite tone of voice for that channel
        for (let i = 0; i < channelTones.length; i++) {
          // Most likely the indexes match up...
          let channel = bot.channels[i];

          // ... but double-check and use fallback approach
          if (channel.channelId !== channelTones[i].channelId) {
            channel = bot.channels.find((c: any) => c.channelId === channelTones[i].channelId);
          }
          if (!channel) continue;

          channel.toneOfVoice = {
            enabled: !!channelTones[i].enabled,
            rules: channelTones[i].rules,
          };
        }
      };

      // Apply for the bo tin botDetails
      applyChannelChanges(bot, channelTones);

      // Redundancy if currentBot is this bot, since it uses a copy rather than reference in botDetails
      if (this.currentBot.uniqueBotId === bot.uniqueBotId) {
        applyChannelChanges(this.currentBot, channelTones);
      }
    },
    /** Converted from legacy mutation `setMarkdownToggle` (no matching legacy action existed). */
    setMarkdownToggle(payload: { botId: string; channelId: string; markdown: boolean }) {
      const { botId, channelId, markdown } = payload;
      const liveBot = this.botDetails[botId];

      const channel = liveBot.channels.find((channel: any) => channel.channelId === channelId);
      if (!channel) return false;

      channel.markdown = !!markdown;
    },

    /**
     * Overwrite the an entire channel in the bot to update its state.
     * Converted from legacy mutation `setChannel` (no matching legacy action existed).
     * @param botId The ID of the bot. Either a live or staging bot ID. You likely want live bot ID.
     * @param channel Full new state of the channel, including its channel ID so it know chich channel to modify
     * @returns Whether the channel was found and updated
     */
    setChannel(payload: { botId: string; channel: any }) {
      const { botId, channel } = payload;
      const bot = this.botDetails[botId];
      if (!bot) return false;

      const chIdx = bot.channels.findIndex((c: any) => c.channelId === channel.channelId);
      if (chIdx === -1) return false;

      bot.channels[chIdx] = channel;
      if (this.currentBot.uniqueBotId === botId) {
        this.currentBot.channels[chIdx] = channel;
      }

      return true;
    },
  },
  vuexPersist: {
    session: true,
  },
});
